/**
 * ModalShell - Reusable modal wrapper with fullscreen toggle and configurable size.
 * Reads modal_size from design settings CSS variable, with fallback to "lg".
 */
import React, { useState, useEffect } from "react";
import { X, Maximize2, Minimize2 } from "lucide-react";
import Portal from "./Portal";

const SIZE_MAP = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  "2xl": "max-w-5xl",
};

export default function ModalShell({ title, onClose, children, footer, testId, size }) {
  const [fullscreen, setFullscreen] = useState(false);

  const configSize = size || (getComputedStyle(document.documentElement).getPropertyValue("--modal-size").trim() || "lg");
  const sizeClass = fullscreen ? "max-w-[96vw] h-[95vh]" : (SIZE_MAP[configSize] || SIZE_MAP.lg);

  // ESC key closes the modal (handles topmost modal first via stopPropagation in nested modals)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && onClose) {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <Portal>
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-3 animate-in fade-in duration-150"
      data-testid={testId || "modal-shell"}
    >
      <div
        className={`bg-white rounded-t-2xl sm:rounded-2xl w-full ${sizeClass} shadow-2xl flex flex-col transition-all duration-200 ${fullscreen ? "" : "max-h-[95vh] sm:max-h-[92vh]"}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100 flex-shrink-0">
          <h2 className="font-bold text-base sm:text-lg text-slate-900 truncate">{title}</h2>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setFullscreen(f => !f)}
              className="hidden sm:inline-flex p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="modal-fullscreen-toggle"
              title={fullscreen ? "Zmenšit" : "Na celou obrazovku"}
            >
              {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-testid="modal-close">
              <X className="w-4.5 h-4.5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-100 flex-shrink-0 flex gap-3 justify-end">
            {footer}
          </div>
        )}
      </div>
    </div>
    </Portal>
  );
}
